import { createApp } from 'vue'
import { createPinia } from 'pinia'
import Antd from 'ant-design-vue'
import zhCN from 'ant-design-vue/es/locale/zh_CN'
import 'ant-design-vue/dist/reset.css'

import App from './App.vue'
import router from './router'
import { authUtils } from './api/auth'
import { clearAllAuthState, isTokenExpired, cleanupCorruptedTokens, handleTokenRefreshFailure } from './utils/authUtils'
import { useBrandStore } from './stores/brand'
import { startTokenRefreshTimer, stopTokenRefreshTimer } from './utils/tokenRefreshTimer'

// 全局优化触摸事件处理
document.addEventListener('DOMContentLoaded', () => {
  // 为所有元素添加触摸优化
  document.body.style.touchAction = 'manipulation'
  
  // 监听所有触摸事件，添加被动监听器
  const addPassiveListener = (eventType: string) => {
    document.addEventListener(eventType, (e) => {
      // 被动事件处理
    }, { passive: true, capture: true })
  }
  
  // 为常见的触摸事件添加被动监听器
  addPassiveListener('touchstart')
  addPassiveListener('touchmove')
  addPassiveListener('touchend')
})

// 扩展 Element 类型以包含 Vue 应用实例
declare global {
  interface Element {
    __vue_app__?: any
  }
}

// 检查是否已经存在应用实例
let app: any = null

// 如果已经存在应用实例，先卸载
const appElement = document.querySelector('#app')
if (appElement?.__vue_app__) {
  console.log('发现已存在的 Vue 应用实例，正在卸载...')
  appElement.__vue_app__.unmount()
  // 清理 DOM 内容
  appElement.innerHTML = ''
}

app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(Antd)

// 全局刷新状态管理
let isRefreshingToken = false
let refreshPromise: Promise<string | null> | null = null

// 导出全局刷新状态管理，供其他模块使用
export const tokenRefreshManager = {
  isRefreshing: () => isRefreshingToken,
  getRefreshPromise: () => refreshPromise,
  setRefreshing: (refreshing: boolean) => {
    isRefreshingToken = refreshing
  },
  setRefreshPromise: (promise: Promise<string | null> | null) => {
    refreshPromise = promise
  }
}

// 应用启动时的认证检查
const initializeApp = async () => {
  try {
    // 只在生产环境中清理损坏的token，开发环境中保持localStorage数据
    if (import.meta.env.PROD) {
      cleanupCorruptedTokens()
    }
    
    // 初始化品牌状态（从localStorage恢复）
    const brandStore = useBrandStore()
    brandStore.initBrandState()
    
    // 检查是否有token
    let token = localStorage.getItem('apiToken')
    const currentUser = localStorage.getItem('currentUser')
    const refreshToken = localStorage.getItem('apiToken_refresh')
    
    let tokenRefreshed = false // 标记是否已经刷新过token
    
    // 如果token不存在或已过期，但有refreshToken，尝试刷新
    if ((!token || isTokenExpiredInMain(token)) && refreshToken) {
      try {
        const newToken = await refreshTokenOnStartup(refreshToken)
        if (newToken) {
          token = newToken
          tokenRefreshed = true // 标记已经刷新过
        } else {
          // 刷新失败，清除所有token并跳转到登录页
          // 延迟跳转，确保应用已经初始化完成
          setTimeout(() => {
            handleTokenRefreshFailure('应用启动', window.location.pathname)
          }, 500)
        }
      } catch (error) {
        // 刷新过程中出错，清除所有token并跳转到登录页
        // 延迟跳转，确保应用已经初始化完成
        setTimeout(() => {
          handleTokenRefreshFailure('应用启动', window.location.pathname)
        }, 500)
      }
    }
    
    // 只有在没有刷新过token且token存在的情况下，才调用authUtils.refreshTokenIfNeeded()
    if (token && currentUser && !tokenRefreshed) {
      // 尝试刷新token和用户信息
      const isValid = await authUtils.refreshTokenIfNeeded()
      if (!isValid) {
        // 清除无效的认证状态（仅在生产环境）
        if (import.meta.env.PROD) {
          clearAllTokensOnStartup()
        }
      }
    }
    
    // 启动token定时刷新检查（仅在已登录时）
    if (token && currentUser) {
      startTokenRefreshTimer()
    }
  } catch (error) {
    // 清除可能损坏的认证状态（仅在生产环境）
    if (import.meta.env.PROD) {
      clearAllTokensOnStartup()
    }
  }
  
  // 启动应用
  try {
    const appElement = document.querySelector('#app')
    if (appElement && !appElement.__vue_app__) {
      console.log('正在挂载 Vue 应用...')
      app.mount('#app')
      console.log('Vue 应用挂载成功')
    } else if (appElement?.__vue_app__) {
      console.log('Vue 应用已经挂载，跳过重复挂载')
    } else {
      console.error('找不到 #app 元素')
    }
  } catch (error) {
    console.error('挂载 Vue 应用时出错:', error)
  }
}

// 检查token是否过期（使用统一的工具函数）
function isTokenExpiredInMain(token: string): boolean {
  return isTokenExpired(token)
}

// 在应用启动时刷新token（带防重复调用保护）
async function refreshTokenOnStartup(refreshToken: string): Promise<string | null> {
  // 如果正在刷新，返回现有的promise
  if (isRefreshingToken && refreshPromise) {
    return refreshPromise
  }
  
  // 开始刷新
  isRefreshingToken = true
  refreshPromise = performRefreshToken(refreshToken)
  
  try {
    const result = await refreshPromise
    return result
  } finally {
    // 刷新完成，重置状态
    isRefreshingToken = false
    refreshPromise = null
  }
}

// 执行实际的刷新操作
async function performRefreshToken(refreshToken: string): Promise<string | null> {
  try {
    // 获取当前选择的品牌ID
    const currentBrandId = getCurrentBrandId()
    
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5002'
    const apiUrl = apiBaseUrl.endsWith('/') 
      ? `${apiBaseUrl}api/auth/refresh`
      : `${apiBaseUrl}/api/auth/refresh`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        refreshToken,
        brandId: currentBrandId 
      })
    })
    
    const data = await response.json()
    
    if (data.success && data.data) {
      const newAccessToken = data.data
      localStorage.setItem('apiToken', newAccessToken)
      return newAccessToken
    } else {
      // 刷新失败时清除所有认证状态并跳转到登录页
      console.warn('❌ 应用启动时Token刷新失败:', data.message || '刷新令牌无效或已过期')
      // 延迟跳转，确保应用已经初始化完成
      setTimeout(() => {
        handleTokenRefreshFailure('应用启动', window.location.pathname)
      }, 500)
      return null
    }
  } catch (error) {
    // 刷新过程中出错时清除所有认证状态并跳转到登录页
    console.error('❌ 应用启动时Token刷新过程中出错:', error)
    // 延迟跳转，确保应用已经初始化完成
    setTimeout(() => {
      handleTokenRefreshFailure('应用启动', window.location.pathname)
    }, 500)
    return null
  }
}

// 获取当前品牌ID
function getCurrentBrandId(): number | null {
  try {
    const currentBrand = localStorage.getItem('currentBrand')
    if (currentBrand) {
      const brandData = JSON.parse(currentBrand)
      return brandData.id || null
    }
    return null
  } catch (error) {
    console.warn('获取当前品牌ID失败:', error)
    return null
  }
}

// 清除所有token（应用启动时）
function clearAllTokensOnStartup(): void {
  // 在开发环境中，跳过清除操作以保持localStorage数据
  if (import.meta.env.DEV) {
    console.log('开发环境：跳过清除token操作，保持localStorage数据')
    return
  }
  clearAllAuthState('应用启动')
}

// 执行应用初始化
initializeApp()
